// One tile of the demo account switcher — the same shape on every step
// (customer type, jurisdiction, org, persona). `trailing` replaces the chevron
// on a step that ends the walk or is disabled.
import type { IconComponent } from '@/components/ap/layout/demoSwitcher/demoAccountIcons';
import { AltArrowRightLinear } from '@assessprep/icons/solar';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

export type DemoAccountTileProps = Omit<ComponentPropsWithoutRef<typeof Button>, 'children'> & {
  icon: IconComponent;
  title: string;
  titleBadge?: ReactNode;
  summary?: ReactNode;
  summaryLines?: 1 | 2;
  trailing?: ReactNode;
  isCurrentUser?: boolean;
};

// The cue on the persona the reader signs in as, shared with the org-tree
// chips. An outline, not a tint: forced-colors drops backgrounds and rings but
// keeps an outline. `outline-solid` undoes the button's `outline-none`, which
// otherwise blanks the outline style.
export const CURRENT_USER_OUTLINE = 'outline-2 outline-offset-2 outline-primary outline-solid';

export const DemoAccountTile = ({
  icon: Icon,
  title,
  titleBadge,
  summary,
  summaryLines = 1,
  trailing,
  isCurrentUser,
  className,
  ...props
}: DemoAccountTileProps) => (
  <Button
    type="button"
    variant="outline"
    aria-current={isCurrentUser || undefined}
    className={cn(
      'h-auto w-full items-center gap-3 whitespace-normal p-4 text-start',
      isCurrentUser && CURRENT_USER_OUTLINE,
      className
    )}
    {...props}
  >
    <span
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ap-primary-a10 text-primary"
      aria-hidden="true"
    >
      <Icon className="size-5" />
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-2">
        <span className="line-clamp-2 min-w-0 font-medium">{title}</span>
        {titleBadge}
      </span>
      {summary && (
        <span
          className={cn(
            'text-xs font-normal text-muted-foreground',
            summaryLines === 2 ? 'line-clamp-2' : 'line-clamp-1'
          )}
        >
          {summary}
        </span>
      )}
    </span>
    {trailing ?? (
      <AltArrowRightLinear aria-hidden="true" className="text-muted-foreground rtl:rotate-180" />
    )}
  </Button>
);
